import { filter, groupBy, isEmpty, sortBy, toPairs } from 'lodash-es';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchInput from '@/components/common/search-input';
import { GroupAdminListItem } from '@/components/groups/group-admin-sectioned-list/group-admin-list-item';
import useChangeGroupMemberRole from '@/hooks/groups/use-change-group-member-role';
import { useGroupMembersWithRemarkName } from '@/hooks/groups/use-group-members';
import useFuseSearch from '@/hooks/search/use-fuse-search';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { type GroupMember, Role } from '@/models/group/group-info';
import { useUserId } from '@/stores/auth';
import SelectableListItem from '@/components/common/selectable-list/selectable-list-item';

interface Props {
  groupId: number;
}

export default function GroupAdminSelectList({ groupId }: Props) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const userId = useUserId();
  const [selectedList, setSelectedList] = useState<GroupMember[] | null>(null);
  const [keyword, setKeyword] = useState<string>('');
  const { members } = useGroupMembersWithRemarkName(groupId);
  const { mutate: changeGroupMemberRole } = useChangeGroupMemberRole(groupId);

  // filter keyword
  const filterMembers = useFuseSearch({
    data: members,
    keys: ['UserNickName', 'GroupMemberName', 'RemarkName'],
    initialReturnAll: true,
    keyword,
  });

  const groupedMembers = useMemo(() => {
    const groupedParis = toPairs(
      groupBy(
        filter(filterMembers, (m) => m.Role === Role.Member),
        (m) => m.Role,
      ),
    );
    return sortBy(groupedParis, ([role]) => -Number(role));
  }, [filterMembers]);

  const handleChangeRole = () => {
    if (isEmpty(selectedList)) return;
    selectedList?.forEach((m) => {
      changeGroupMemberRole({ userId: m.UserID, role: 9 });
    });
    setSelectedList(null);
  };

  const handlePromptChangeRole = async (member: GroupMember) => {
    if (selectedList?.some((selected) => selected.UserID === member.UserID)) {
      setSelectedList(selectedList.filter((selected) => selected.UserID !== member.UserID));
      return;
    } else {
      setSelectedList([...selectedList || [], member]);
    }
  };

  return (
    <>
      <SearchInput
        className='desktop:mx-0 mx-5 my-4'
        keyword={keyword}
        setKeyword={setKeyword}
        clearKeyword={keyword ? () => setKeyword('') : undefined}
      />

      <ul className='px-5 mb-safe-or-4 flex flex-col gap-5 desktop:overflow-y-auto desktop:mx-0 desktop:px-4 flex-1'>
        {!isEmpty(groupedMembers) ? (
          groupedMembers.map(([key, members]) => (
            <li
              key={key}
              className='first:border-b first:pb-5 only:border-none'>
              <ul className='flex flex-col items-stretch gap-4'>
                {members?.map((m) => (
                  <li
                    key={m.ID}
                    className='flex items-center gap-2'>
                    <GroupAdminListItem
                      member={m}
                      canManage={m.UserID !== userId}
                      onClick={() => handlePromptChangeRole(m)}
                    />

                    <div>
                      <SelectableListItem
                        isSelectable={true}
                        isSelected={selectedList?.some((selected) => selected.UserID === m.UserID)}
                        onSelected={() => handlePromptChangeRole(m)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </li>
          ))
        ) : (
          <div className='text-center mt-24 text-secondary'>
            {t('conversation.setting.group.admin.check-no-member')}
          </div>
        )}
      </ul>

      <button
        type='button'
        disabled={isEmpty(selectedList)}
        className='btn btn-primary btn-md mx-5'
        onClick={handleChangeRole}>
        {t('conversation.setting.group.admin.to-admin')}
      </button>
    </>
  );
}
